/**
 * Modal simplificado para editar card e mover entre stages
 * Complementa PipelineLeadModal com funcionalidade de move direto
 */

import { useMemo, useState } from 'react';
import { Modal, Form, Input, InputNumber, Select, Button, message, Spin } from 'antd';
import { usePipelineBoard } from '../hooks/usePipelineBoard';
import { PipelineCard } from '../types';
import { supabase } from '@/integrations/supabase/client';

interface PipelineCardEditModalProps {
  card: PipelineCard | null;
  pipelineId: string;
  onClose: () => void;
  onSave?: () => void;
}

export function PipelineCardEditModal({
  card,
  pipelineId,
  onClose,
  onSave,
}: PipelineCardEditModalProps) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedStageId, setSelectedStageId] = useState<string>('');
  const [isModified, setIsModified] = useState(false);
  const { kanbanBoard, moveCardAsync } = usePipelineBoard(pipelineId);

  // Opções de stage
  const stageOptions = useMemo(() => {
    if (!kanbanBoard) return [];
    return kanbanBoard.stages.map((swc) => ({
      value: swc.stage.id,
      label: swc.stage.nome,
    }));
  }, [kanbanBoard]);

  // Inicializar form com dados do card
  const open = !!card;

  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      let movedCard = false;
      let updatedData = false;

      // Se selecionou nova stage, mover card
      if (selectedStageId && card && selectedStageId !== card.stage_id) {
        await moveCardAsync({
          cardId: card.id,
          pipelineId,
          stageId: selectedStageId,
          stagAnteriorId: card.stage_id,
        });
        movedCard = true;
      }

      // Atualizar dados do card (nome, telefone, interesse, valor)
      if (card && (values.nome || values.telefone || values.interesse || values.valor)) {
        const novosDados = {
          ...card.dados_card,
          nome: values.nome,
          telefone: values.telefone,
          interesse: values.interesse,
          valor: values.valor,
        };

        await supabase
          .from('pipeline_cards')
          .update({ dados_card: novosDados })
          .eq('id', card.id);

        updatedData = true;
      }

      // Mostrar um único toast consolidado
      if (movedCard || updatedData) {
        const messages = [];
        if (movedCard) messages.push('movido');
        if (updatedData) messages.push('atualizado');
        const successMessage = `Card ${messages.join(' e ')} com sucesso!`;
        message.success(successMessage);
      }

      onSave?.();
      onClose();
    } catch (error: any) {
      message.error(`Erro ao salvar: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Resetar form quando modal fecha
  const handleClose = () => {
    form.resetFields();
    setSelectedStageId('');
    setIsModified(false);
    onClose();
  };

  return (
    <Modal
      title={card ? `Editar Lead: ${card.dados_card?.nome || '—'}` : 'Editar Card'}
      open={open}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Cancelar
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={loading}
          onClick={handleSave}
          disabled={!selectedStageId && !isModified}
        >
          Salvar
        </Button>,
      ]}
      width={500}
    >
      {!card ? (
        <Spin />
      ) : (
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            nome: card.dados_card?.nome || '',
            telefone: card.dados_card?.telefone || '',
            interesse: card.dados_card?.interesse || '',
            valor: card.dados_card?.valor || undefined,
          }}
          onValuesChange={() => setIsModified(true)}
        >
          <Form.Item label="Nome" name="nome">
            <Input placeholder="Nome do lead/cliente" />
          </Form.Item>

          <Form.Item label="Telefone" name="telefone">
            <Input placeholder="Telefone" />
          </Form.Item>

          <Form.Item label="Interesse" name="interesse">
            <Input.TextArea
              placeholder="Descreva o interesse do cliente"
              rows={2}
            />
          </Form.Item>

          <Form.Item label="Valor" name="valor">
            <InputNumber
              min={0}
              step={100}
              formatter={(value) =>
                new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                }).format(value || 0)
              }
              parser={(value) => {
                const num = parseFloat(value?.replace(/\D/g, '') || '0');
                return (num / 100) as any;
              }}
              placeholder="Valor do negócio"
            />
          </Form.Item>

          <Form.Item
            label="Mover para Stage"
            required
            rules={[{ required: true, message: 'Selecione uma stage' }]}
          >
            <Select
              placeholder="Selecione uma etapa"
              value={selectedStageId || undefined}
              onChange={setSelectedStageId}
              options={stageOptions}
            />
          </Form.Item>

          <Form.Item label="Informações do Card">
            <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
              <div>
                <span className="font-medium">ID:</span> {card.id}
              </div>
              <div>
                <span className="font-medium">Stage Atual:</span>{' '}
                {kanbanBoard?.stages
                  .find((s) => s.stage.id === card.stage_id)
                  ?.stage.nome || '—'}
              </div>
              <div>
                <span className="font-medium">Criado em:</span>{' '}
                {new Date(card.data_criacao).toLocaleDateString('pt-BR')}
              </div>
            </div>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
}

export default PipelineCardEditModal;
